import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, TransportCardLayout } from './common/DetailCardAndPinLayout'
import TransportTimeline from './common/TransportTimeline'
import ItemTitle from './common/ItemTitle'
import Attachments from './common/Attachments'
import type { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { useAirlineByCode, useAirlineByName, useAirport } from 'tripPlanner/hooks/api/flights'
import type { FlightItem } from 'tripPlanner/types/tripItem'
import { isLeBookmark } from 'tripPlanner/utils'
import { formatPipeSeparator } from 'LuxKit/intl/separators'
import FlightCTAContent from './CTAContent/FlightCTAContent'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import { rem } from 'polished'
import TransportDefaultLogo from './common/TransportDefaultLogo'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'

function getAirportName(
  airport: App.AirportLocation | undefined,
  placeName: string,
) {
  let airportName: string | undefined

  if (airport) {
    airportName =
      airport.airportName || airport.cityAirportName || airport.cityDesc
  }
  // Fallback if no airport or if it's missing data
  if (!airportName) {
    airportName = placeName
  }
  return airportName
}

const Logo = styled.img`
  width: ${rem(56)};
  height: ${rem(56)};
  object-fit: contain;
`

interface Props {
  item: FlightItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailFlightCard({ item, variant }: Props) {
  const intl = useIntl()
  const { fareCabinLabelMap } = useFlightFareLabels()
  const [inFocus, setInFocus] = useState(false)
  const [showComment, setShowComment] = useState<boolean>(!!item.notes)

  const { data: trip } = useTrip({ tripId: item.tripId })

  // Using 'airlineCode' to reliably lookup airline images since some
  // airline names returned by api.flightstats.com doesn't always map to an airline in svc-flights
  const matches = item.flightNumber?.match(/^([A-Z]{2})/)
  const airlineCode = matches ? matches[1] : undefined
  const { data: airlineByCode } = useAirlineByCode({
    code: airlineCode,
  })
  const { data: airlineByName } = useAirlineByName({
    airlineName: item.airlineName,
    enabled: !airlineCode,
  })
  const airline = airlineByCode ?? airlineByName

  const airlineLogoUrl = airline?.airlineLogo
  const logoImg = airlineLogoUrl ? <Logo src={airlineLogoUrl} /> : <TransportDefaultLogo type="FLIGHT" />

  const { data: departureAirport, isLoading: isDepartureAirportLoading } =
    useAirport({
      code: item.originAirportCode,
    })

  const { data: arrivalAirport, isLoading: isArrivalAirportLoading } =
    useAirport({
      code: item.destinationAirportCode,
    })

  const departureAirportName = getAirportName(
    departureAirport,
    item.startPlace.name,
  )
  const arrivalAirportName = getAirportName(arrivalAirport, item.endPlace.name)
  const hasAirportNames = !!(departureAirportName && arrivalAirportName)
  const isLoadingAirportData =
    isDepartureAirportLoading || isArrivalAirportLoading

  const title = hasAirportNames ? (
    <FormattedMessage
      id="EwH4AzrKSgyGVN4Gvyw8"
      defaultMessage="{departure} {icon} {arrival}"
      description="Flight route showing departure and arrival airport names with an arrow icon between them"
      values={{
        departure: departureAirportName,
        icon: <LineArrowRightIcon size="S" />,
        arrival: arrivalAirportName,
      }}
    />
  ) : isLoadingAirportData ? (
    <TextLoadingBox width={160} />
  ) : (
    intl.formatMessage({
      id: 'wSs9VQdaaEZRFkSMLMUM',
      defaultMessage: 'Flight',
      description: 'Fallback title for a flight card when airport names are unavailable',
    })
  )

  const fareClass = item.savedItemData ?
    fareCabinLabelMap[item.savedItemData.fareClass as keyof typeof fareCabinLabelMap] :
    undefined

  const travellerCount: App.Occupants = item.savedItemData?.travellerCounts ?? {
    adults: 0,
  }

  const details = [
    item.flightNumber,
    fareClass,
    formatOccupantsShort(travellerCount),
  ]
    .filter(Boolean)
    .join(formatPipeSeparator(intl))

  const { itemNumber, itemColor } = useMemo(
    () => getItemPinBadge(item, variant, trip),
    [item, variant, trip],
  )

  const onFocus = useCallback(() => {
    setInFocus(true)
  }, [])

  const onBlur = useCallback(() => {
    setInFocus(false)
  }, [])

  const onRequestAddComment = useCallback(() => {
    setShowComment(true)
    setInFocus(true)
  }, [])

  const onNotesChange = useCallback((notes: string | undefined) => {
    setShowComment(!!notes)
  }, [])

  return (
    <DetailCardAndPinLayout data-testid="detail-card-flight">
      <DetailCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <TransportCardLayout>
        <TransportCardLayout.Image>
          <HoverOnlyRegenerateItemButton item={item} />
          {logoImg}
        </TransportCardLayout.Image>
        <TransportCardLayout.Content>
          <CornerControls item={item} onRequestAddComment={onRequestAddComment} />
          <VerticalSpacer gap={12}>
            <VerticalSpacer gap={4}>
              <ItemTitle>{title}</ItemTitle>
              {details && (
                <BodyText variant="small" colour="neutral-two">
                  {details}
                </BodyText>
              )}
            </VerticalSpacer>
            <Attachments item={item} />
          </VerticalSpacer>
        </TransportCardLayout.Content>
        <TransportCardLayout.Timeline>
          <TransportTimeline
            item={item}
            originLabel={item.originAirportCode}
            destinationLabel={item.destinationAirportCode}
            variant={variant}
          />
          {isLeBookmark(item) && <FlightCTAContent item={item} variant={variant} />}
        </TransportCardLayout.Timeline>
        {showComment && (
          <TransportCardLayout.Comment>
            <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              inFocus={inFocus}
              onFocus={onFocus}
              onBlur={onBlur}
              onNotesChange={onNotesChange}
            />
          </TransportCardLayout.Comment>
        )}
      </TransportCardLayout>
    </DetailCardAndPinLayout>
  )
}

export default DetailFlightCard
